import { type Ref } from "vue";

import { useBrowserStore } from "@/composables/useBrowserStore";
import type { UseProfile } from "@/composables/useProfile";
import { in_electron } from "@/utils/in";
import { MICROPHONE_MONITOR_IPC } from "@/utils/native/microphone-monitor/ipc";

export const MEETING_DETECTION_KEY = "preferences/meetings/detection:enabled";

// App-lifetime singleton: the synced state (and its detached persistence
// watcher) is deliberately never released — it is cached at module level and
// must keep persisting after the first calling component unmounts. Safe to
// key per account: a session only ever has one account, so every caller
// resolves the same store.
let meetingDetectionPreference: Ref<boolean> | undefined;
let meetingDetectionIpcInitialized = false;
let sendMeetingDetectionPreference: ((enabled: boolean) => void) | undefined;

function initializeMeetingDetectionIpc(meetingDetection: Ref<boolean>) {
  if (!in_electron || meetingDetectionIpcInitialized) return;
  meetingDetectionIpcInitialized = true;

  const { ipcRenderer: ipc } = require("electron");
  sendMeetingDetectionPreference = (enabled) => {
    ipc.send(MICROPHONE_MONITOR_IPC.SET_ENABLED, enabled);
  };
  ipc.on(
    MICROPHONE_MONITOR_IPC.ENABLED_CHANGED,
    (_event: unknown, enabled: unknown) => {
      if (typeof enabled === "boolean") {
        meetingDetection.value = enabled;
      }
    }
  );
  // Let the caller finish registering its mic-state listener before the main
  // process starts a monitor that can emit its initial state immediately.
  queueMicrotask(() => {
    sendMeetingDetectionPreference?.(meetingDetection.value);
  });
}

export function useMeetingDetection(profile: Ref<UseProfile>) {
  // Namespaced per account (adopting the value written un-namespaced by
  // older versions on first read).
  const { migrate, syncState } = useBrowserStore(profile.value.user.accountId);
  migrate(MEETING_DETECTION_KEY);
  const meetingDetection =
    meetingDetectionPreference ??
    (meetingDetectionPreference = syncState<boolean>(
      MEETING_DETECTION_KEY,
      true
    ));
  initializeMeetingDetectionIpc(meetingDetection);
  const toggleMeetingDetection = (value: boolean) => {
    meetingDetection.value = value;
    sendMeetingDetectionPreference?.(value);
  };

  return {
    meetingDetection,
    toggleMeetingDetection,
  };
}
